import clsx from 'clsx';
import type React from 'react';
import type ZorgbaseResult from '@/lib/models/zorgbase/ZorgbaseResult';
import { translateZorgbaseResult } from '@/lib/models/zorgbase/ZorgbaseResult';
import { getOrganisationDisplayName, sanitizeMarkup } from '@/lib/util';

import styles from './OrganisationResult.module.scss';

export interface OrganisationResultProps {
	result: ZorgbaseResult;
	locale: string;

	// biome-ignore lint/suspicious/noExplicitAny: next-intl Translator<T> can't be expressed as (key: string) => string due to contravariance
	t: (key: any) => string;
	onMouseOver?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onMouseOut?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function OrganisationResult({
	result,
	locale,
	t,
	// onMouseOver,
	// onMouseOut
}: OrganisationResultProps) {
	const item = translateZorgbaseResult(result, locale);

	return (
		<div className={styles.OrganisationResult} data-id={item.id}>
			<h3>{getOrganisationDisplayName(item)}</h3>

			<div className={clsx('flex-row', styles.legend)}>
				{item.acceptsPatients === 1 && (
					<div className={clsx('col-6', styles.registration)}>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img src="/icons/patient.svg" alt={`icon ${t('MapDisplay.OpenToPatients')}`} />
						<div>{t('MapDisplay.OpenToPatients')}</div>
					</div>
				)}
				{item.acceptsPatients === 0 && (
					<div className={clsx('col-6', styles.noRegistration)}>
						{/* <img src="/icons/patient.svg" alt={`icon ${t('MapDisplay.ClosedToPatients')}`} /> */}
						<div>{t('MapDisplay.ClosedToPatients')}</div>
					</div>
				)}

				{item.acceptsPassersBy === 1 && (
					<div className={clsx('col-6', styles.passersBy)}>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img src="/icons/passant.svg" alt={`icon ${t('MapDisplay.OpenToPassersBy')}`} />
						<div>{t('MapDisplay.OpenToPassersBy')}</div>
					</div>
				)}

				{item.acceptsPatients == null && (
					<div className={clsx('col-6', styles.unknown)}>
						{/* <img src="/icons/passant.svg" alt={`icon ${t('MapDisplay.UnknownIfOpenToPassersBy')}`} /> */}
						<div>{t('MapDisplay.Unknown')}</div>
					</div>
				)}
			</div>

			<div className={styles.location}>
				<div dangerouslySetInnerHTML={sanitizeMarkup(item.addressString)} />

				<div>
					{/* eslint-disable-next-line @next/next/no-img-element */}
					<img src="/icons/location.svg" alt="distance" />
					<small>{`${item.distance} km`}</small>
				</div>
			</div>

			<div className={clsx('flex-row', styles.contact)}>
				{item.workPhonenumber && item.workPhonenumber.substring(0, 2) !== '06' && (
					<div className="col-6">
						<a href={`tel:${item.workPhonenumber}`} className="text-decoration-none">
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src="/icons/phone.svg" alt="telefoonnummer" />
							{item.workPhonenumber}
						</a>
					</div>
				)}

				{item.url && (
					<div className="col-6">
						<a href={item.url} className="text-decoration-none" target="_blank" rel="noreferrer">
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src="/icons/arrow-right.svg" alt="website" />
							website
						</a>
					</div>
				)}
			</div>

			{item.acceptsPatientsRestrictions && (
				<>
					<h4>{t('MapDisplay.ConditionsForPatients')}</h4>
					<p dangerouslySetInnerHTML={sanitizeMarkup(item.acceptsPatientsRestrictions)} />
				</>
			)}

			{item.acceptsPassersByRestrictions && (
				<>
					<h4>{t('MapDisplay.ConditionsForPassersBy')}</h4>
					<p dangerouslySetInnerHTML={sanitizeMarkup(item.acceptsPassersByRestrictions)} />
				</>
			)}

			{item.acceptsPatientsUrl && (
				<p>
					<a href={item.acceptsPatientsUrl} className="button primary" target="_blank" rel="noreferrer">
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img src="/icons/register.svg" alt="meer informatie" />
						{t('MapDisplay.MeerInformatie')}
					</a>
				</p>
			)}

			{item.lastUpdated && <small className="faded">{`${t('MapDisplay.LastUpdated')} ${item.lastUpdated}`}</small>}
		</div>
	);
}
